/**
 * Modern Propane — storefront GSAP animations
 * Hero entrance + scroll reveals (products, delivery blocks, store cards).
 * Excludes cart, ZIP topbar, drawers. Respects prefers-reduced-motion.
 * Debug: append ?gsapDebug=1 to the URL.
 */
(function () {
  'use strict';

  var EXCLUDE =
    '[data-mp-no-gsap], .cart-popup-wrapper, .cart-popup, .drawer, #SearchDrawer, .mp-topbar';

  var debug =
    typeof URLSearchParams !== 'undefined' &&
    new URLSearchParams(window.location.search).get('gsapDebug') === '1';

  var initialized = false;
  var reducedMotionMq =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function log() {
    if (typeof console !== 'undefined' && console.log) {
      console.log.apply(console, ['[Modern Propane GSAP]'].concat([].slice.call(arguments)));
    }
  }

  function debugLog() {
    if (debug) log.apply(null, arguments);
  }

  function prefersReducedMotion() {
    return !!(reducedMotionMq && reducedMotionMq.matches);
  }

  function isExcluded(el) {
    return !!(el && el.closest && el.closest(EXCLUDE));
  }

  /** @param {string} selector @param {ParentNode} [root] @returns {Element[]} */
  function querySafe(selector, root) {
    root = root || document;
    var nodes = root.querySelectorAll(selector);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (!isExcluded(nodes[i])) out.push(nodes[i]);
    }
    return out;
  }

  function countTargets() {
    return {
      heroes: querySafe('[data-gsap-hero]').length,
      heroItems: querySafe('[data-gsap-hero-item]').length,
      heroMedia: querySafe('[data-gsap-hero-media]').length,
      productCards: querySafe('[data-gsap-product-card]').length,
      reveals: querySafe('[data-gsap-reveal]').length,
      storeCards: querySafe('[data-gsap-store-card]').length,
      footerHeadings: querySafe('[data-gsap-footer-heading]').length,
    };
  }

  function applyDebugOutlines() {
    if (!debug) return;
    var selectors =
      '[data-gsap-hero], [data-gsap-hero-item], [data-gsap-hero-media], ' +
      '[data-gsap-product-card], [data-gsap-reveal], [data-gsap-store-card], [data-gsap-footer-heading]';
    querySafe(selectors).forEach(function (el) {
      el.style.outline = '2px dashed #1a6bff';
      el.style.outlineOffset = '2px';
    });
    document.documentElement.classList.add('mp-gsap-debug');
  }

  function cleanup() {
    initialized = false;
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.getAll().forEach(function (st) {
        st.kill();
      });
    }
    if (typeof gsap !== 'undefined') {
      gsap.killTweensOf(
        querySafe(
          '[data-gsap-hero-item], [data-gsap-hero-media], [data-gsap-reveal], [data-gsap-product-card], [data-gsap-store-card], .mp-footer-heading__line'
        )
      );
    }
    document.documentElement.classList.remove('mp-gsap-ready', 'mp-gsap-debug');
  }

  function resetAnimatedTargets() {
    var targets = querySafe(
      '[data-gsap-hero-item], [data-gsap-hero-media], [data-gsap-reveal], [data-gsap-product-card], [data-gsap-store-card]'
    );
    var footerLines = querySafe('.mp-footer-heading__line');
    if (targets.length && typeof gsap !== 'undefined') {
      gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
    }
    if (footerLines.length && typeof gsap !== 'undefined') {
      gsap.set(footerLines, { clearProps: 'transform' });
    }
  }

  function markReady() {
    document.documentElement.classList.add('mp-gsap-ready');
  }

  /** Wait until critical CSS no longer hides #MainContent (opacity 0). */
  function whenMainContentVisible(callback) {
    var main = document.getElementById('MainContent');
    if (!main) {
      callback();
      return;
    }

    var attempts = 0;
    var maxAttempts = 120;

    function check() {
      attempts += 1;
      var opacity = parseFloat(window.getComputedStyle(main).opacity);
      if (opacity >= 0.99 || attempts >= maxAttempts) {
        requestAnimationFrame(callback);
        return;
      }
      requestAnimationFrame(check);
    }

    requestAnimationFrame(check);
  }

  function initHero() {
    var heroes = querySafe('[data-gsap-hero]');
    if (!heroes.length) {
      debugLog('initHero: no [data-gsap-hero] found');
      return;
    }

    debugLog('initHero:', heroes.length, 'hero section(s)');

    heroes.forEach(function (hero) {
      var items = hero.querySelectorAll('[data-gsap-hero-item]');
      var media = hero.querySelector('[data-gsap-hero-media]');
      if (!items.length && !media) return;

      var isHomeIndexHero =
        document.body.classList.contains('template-index') &&
        hero.classList.contains('mp-home-hero');

      var tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onStart: function () {
          debugLog('hero timeline started');
        },
      });

      if (media) {
        if (isHomeIndexHero) {
          gsap.set(media, { opacity: 0 });
          tl.to(media, { opacity: 1, duration: 0.9, ease: 'power3.out' }, 0);
        } else {
          gsap.set(media, { opacity: 0, scale: 0.96, y: 18, transformOrigin: '50% 50%' });
          tl.to(
            media,
            { opacity: 1, scale: 1, y: 0, duration: 0.9, ease: 'power3.out' },
            0
          );
        }
        debugLog('initHero media');
      }

      if (items.length) {
        if (isHomeIndexHero) {
          gsap.set(items, { opacity: 0, y: 0 });
          tl.to(
            items,
            {
              opacity: 1,
              duration: 0.65,
              stagger: 0.08,
              ease: 'power2.out',
              onComplete: function () {
                gsap.set(items, { clearProps: 'transform' });
              },
            },
            media ? 0.05 : 0
          );
        } else {
          gsap.set(items, { opacity: 0, y: 32 });
          tl.to(
            items,
            {
              opacity: 1,
              y: 0,
              duration: 0.75,
              stagger: 0.12,
              ease: 'power3.out',
            },
            media ? 0.08 : 0
          );
        }
        debugLog('initHero items:', items.length);
      }
    });
  }

  /**
   * @param {string} selector
   * @param {string} label
   * @param {{ fromY: number, duration: number, stagger: number, batchMax: number, interval: number }} opts
   */
  function initBatchReveal(selector, label, opts) {
    var elements = querySafe(selector);
    if (!elements.length) {
      debugLog('initBatchReveal skipped (0 matches):', label, selector);
      return;
    }

    debugLog('initBatchReveal:', label, elements.length, 'element(s)');

    gsap.set(elements, { opacity: 0, y: opts.fromY });

    ScrollTrigger.batch(elements, {
      interval: opts.interval,
      batchMax: opts.batchMax,
      start: 'top 85%',
      once: true,
      onEnter: function (batch) {
        debugLog('batch enter:', label, batch.length);
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: opts.duration,
          stagger: opts.stagger,
          ease: 'power3.out',
          overwrite: true,
        });
      },
    });
  }

  function initFooterHeadingLines() {
    var headings = querySafe('[data-gsap-footer-heading]');
    if (!headings.length) {
      debugLog('initFooterHeadingLines: no headings found');
      return;
    }

    var lines = [];
    headings.forEach(function (heading) {
      var line = heading.querySelector('.mp-footer-heading__line');
      if (!line) return;
      lines.push(line);
      gsap.set(line, { scaleX: 0, transformOrigin: 'left center' });
    });

    if (!lines.length) return;

    debugLog('initFooterHeadingLines:', lines.length, 'line(s)');

    ScrollTrigger.batch(headings, {
      interval: 0.08,
      batchMax: 6,
      start: 'top 92%',
      once: true,
      onEnter: function (batch) {
        batch.forEach(function (heading, index) {
          var line = heading.querySelector('.mp-footer-heading__line');
          if (!line) return;
          gsap.to(line, {
            scaleX: 1,
            duration: 0.7,
            delay: index * 0.1,
            ease: 'power3.out',
            overwrite: true,
          });
        });
      },
    });
  }

  function initScrollReveals() {
    initBatchReveal('[data-gsap-product-card]', 'product-cards', {
      fromY: 36,
      duration: 0.7,
      stagger: 0.1,
      batchMax: 6,
      interval: 0.12,
    });

    initBatchReveal('[data-gsap-reveal]', 'reveal-blocks', {
      fromY: 36,
      duration: 0.7,
      stagger: 0.12,
      batchMax: 4,
      interval: 0.14,
    });

    initBatchReveal('[data-gsap-store-card]', 'store-cards', {
      fromY: 36,
      duration: 0.7,
      stagger: 0.14,
      batchMax: 2,
      interval: 0.16,
    });

    initFooterHeadingLines();
  }

  function runAnimations() {
    var started = false;

    function start() {
      if (started) return;
      started = true;

      whenMainContentVisible(function () {
        initHero();
        initScrollReveals();
        markReady();
        initialized = true;
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
        applyDebugOutlines();
        debugLog('animations initialized');
      });
    }

    if (
      document.body.classList.contains('template-index') &&
      !document.body.classList.contains('mp-index-chrome-locked')
    ) {
      document.addEventListener('mp-index-chrome-stable', start, { once: true });
      setTimeout(start, 1000);
    } else {
      start();
    }
  }

  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      debugLog('GSAP or ScrollTrigger missing — animations disabled');
      resetAnimatedTargets();
      markReady();
      return;
    }

    debugLog('Modern Propane GSAP loaded');
    debugLog('window.gsap:', typeof window.gsap !== 'undefined');
    debugLog('window.ScrollTrigger:', typeof window.ScrollTrigger !== 'undefined');

    var counts = countTargets();
    debugLog('hero sections:', counts.heroes);
    debugLog('hero items:', counts.heroItems);
    debugLog('hero media:', counts.heroMedia);
    debugLog('product cards:', counts.productCards);
    debugLog('reveal blocks:', counts.reveals);
    debugLog('store cards:', counts.storeCards);
    debugLog('footer headings:', counts.footerHeadings);

    if (debug) {
      debugLog('Debug mode ON (?gsapDebug=1) — outlines + init logs enabled');
    }

    cleanup();
    gsap.registerPlugin(ScrollTrigger);

    if (prefersReducedMotion()) {
      debugLog('prefers-reduced-motion: reduce — skipping animations');
      resetAnimatedTargets();
      markReady();
      return;
    }

    debugLog('motion allowed — running animations');
    runAnimations();
  }

  function boot() {
    try {
      init();
    } catch (err) {
      resetAnimatedTargets();
      markReady();
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Modern Propane GSAP]', err);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (reducedMotionMq && reducedMotionMq.addEventListener) {
    reducedMotionMq.addEventListener('change', function () {
      requestAnimationFrame(boot);
    });
  } else if (reducedMotionMq && reducedMotionMq.addListener) {
    reducedMotionMq.addListener(function () {
      requestAnimationFrame(boot);
    });
  }

  document.addEventListener('shopify:section:load', function () {
    requestAnimationFrame(boot);
  });

  document.addEventListener('shopify:section:unload', cleanup);

  document.addEventListener('shopify:section:reorder', function () {
    requestAnimationFrame(function () {
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
  });
})();
